import React, { useState, useEffect, useCallback, useRef } from 'react';
import './ModalStatusLed.css';

/**
 * Modal Status LED - A small indicator showing Modal GPU container state.
 *
 * Displays in the top-right corner of the MiniMe app to show photographers
 * whether the background removal GPU service is ready.
 *
 * Colors:
 * - Green: GPU is warm and ready (מודל מוכן)
 * - Yellow: GPU is waking up (מחמם...)
 * - Blue: GPU is cold — normal scale-to-zero, double-click to warm (מודל קר)
 * - Red: error / service unavailable / no live worker listening on the MiniMe
 *        queue (photos would wait in RQ) — a real problem, not a cold container
 * - Gray: Unknown state or not configured
 *
 * Behavior:
 * - Passive check every 30s (doesn't wake GPU, free)
 * - Single click: Show status tooltip for 3 seconds
 * - Double click: Wake up GPU (triggers cold start if needed)
 */
const ModalStatusLed = ({ serverUrl }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [waking, setWaking] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const clickTimeoutRef = useRef(null);
  const tooltipTimeoutRef = useRef(null);

  // Passive status check - doesn't wake GPU
  const checkStatus = useCallback(async () => {
    if (!serverUrl) return;

    setLoading(true);
    try {
      const response = await fetch(`${serverUrl}/api/minime/modal-status`, {
        credentials: 'include'
      });
      const data = await response.json();
      setStatus(data);
    } catch (error) {
      console.warn('Failed to check Modal status:', error);
      setStatus({ status: 'error', container_state: 'unavailable' });
    }
    setLoading(false);
  }, [serverUrl]);

  // Wake up GPU - triggers cold start if needed
  const wakeGpu = useCallback(async () => {
    if (!serverUrl || waking) return;

    setWaking(true);
    try {
      const response = await fetch(`${serverUrl}/api/minime/modal-wake`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      // Functional update — never clobber a fresher poll with this callback's stale
      // closure snapshot; then re-poll for the fields wake doesn't return (queue health).
      setStatus((prev) => ({
        ...prev,
        container_state: data.container_state,
        response_time_ms: data.response_time_ms,
        is_warm: data.container_state === 'warm'
      }));
      checkStatus();
    } catch (error) {
      console.warn('Failed to wake Modal GPU:', error);
    }
    setWaking(false);
  }, [serverUrl, waking, checkStatus]);

  useEffect(() => {
    checkStatus();
    // Passive check every 30 seconds (free, doesn't wake GPU)
    const interval = setInterval(checkStatus, 30000);
    return () => clearInterval(interval);
  }, [checkStatus]);

  const getStatusColor = () => {
    if (!status || status.status === 'not_configured') return '#808080'; // Gray
    if (waking) return '#eab308'; // Yellow while waking
    // Honest LED: no live worker on the routed queue means photos will NOT process
    // no matter how warm the GPU is — a real problem, not a cold container.
    // (=== false on purpose: null means the server couldn't check — don't punish.)
    if (status.queue_has_listener === false) return '#ef4444';
    if (status.is_warm || status.container_state === 'warm') return '#22c55e'; // Green
    // Server-reported warming (event pin raised, container still booting) — same
    // yellow as the local wake flow; without this mapping it fell through to red.
    if (status.container_state === 'warming') return '#eab308';
    if (status.container_state === 'cold') return '#3b82f6'; // Blue when cold (normal, not an error)
    // Unknown state (no activity data yet) - show gray (neutral)
    if (status.container_state === 'unknown') return '#808080';
    return '#ef4444'; // Red only for unavailable / error
  };

  const getTooltipText = () => {
    if (waking) return 'מחמם את המודל...';
    if (!status) return 'בודק מצב מודל...';
    if (status.status === 'not_configured') return 'מודל לא מוגדר';
    if (status.queue_has_listener === false) return 'אין עובד זמין לעיבוד — תמונות ימתינו בתור';
    if (status.is_warm || status.container_state === 'warm') return 'מודל מוכן';
    if (status.container_state === 'warming') return 'המודל מתחמם - מוכן בעוד כדקה';
    if (status.container_state === 'cold') return 'מודל קר - לחיצה כפולה לחימום';
    if (status.container_state === 'unknown') return 'מצב לא ידוע - לחיצה כפולה להפעלה';
    return 'מודל לא זמין';
  };

  // Show tooltip for 3 seconds
  const showTooltipTemporarily = () => {
    // Clear any existing tooltip timeout
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }

    setShowTooltip(true);
    tooltipTimeoutRef.current = setTimeout(() => {
      setShowTooltip(false);
    }, 3000);
  };

  // Handle single click - show tooltip
  const handleSingleClick = () => {
    showTooltipTemporarily();
    checkStatus(); // Also refresh status
  };

  // Handle double click - wake GPU
  const handleDoubleClick = () => {
    // Clear single click timeout to prevent showing tooltip
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
    }

    // Wake GPU regardless of state
    wakeGpu();
  };

  const handleClick = (e) => {
    e.preventDefault();

    // If already waking, ignore clicks
    if (waking) return;

    // Use timeout to differentiate single vs double click
    if (clickTimeoutRef.current) {
      // Second click within 300ms = double click
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
      handleDoubleClick();
    } else {
      // First click - wait to see if there's a second
      clickTimeoutRef.current = setTimeout(() => {
        clickTimeoutRef.current = null;
        handleSingleClick();
      }, 300);
    }
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current);
      if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);
    };
  }, []);

  return (
    <div
      className="modal-status-led-container"
      onClick={handleClick}
    >
      <div
        className={`modal-status-led ${loading || waking ? 'pulsing' : ''}`}
        style={{ backgroundColor: getStatusColor() }}
      />
      {showTooltip && (
        <div className="modal-status-tooltip">
          {getTooltipText()}
        </div>
      )}
    </div>
  );
};

export default ModalStatusLed;
